import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { MainLayout } from '@/components/layout/MainLayout';
import { BackButton } from '@/components/ui/back-button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { authApi } from '@/lib/pillarApi';
import { sessionSensitiveAuthApi } from '@/lib/sessionSensitiveAuthApi';
import { runInBackground } from '@/lib/utils';
import { useAuthStore } from '@/store/auth';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import {
    normalizeUserEmailInputForSubmission,
    SessionUserProfileDetailedResponse,
    SessionUserProfileDetailedResponseSchema,
    UserEmailIdentitySchema,
} from '@anchorbase/pillar-sdk';
import { createComponentLogger } from '@/lib/logging/frontend-logger';

function EmailsContent() {
    const log = createComponentLogger('ProfileEmailsPage');
    const queryClient = useQueryClient();
    const { user } = useAuthStore();
    const [newEmail, setNewEmail] = useState('');
    const [showAddForm, setShowAddForm] = useState(false);
    const [addEmailMessage, setAddEmailMessage] = useState<string | null>(null);
    const [showRemoveEmailConfirm, setShowRemoveEmailConfirm] = useState(false);
    const [emailToRemove, setEmailToRemove] = useState<string | null>(null);

    const {
        data: profile,
        isLoading,
        error,
    } = useQuery<SessionUserProfileDetailedResponse | null>({
        queryKey: ['profile'],
        queryFn: async () => {
            const response = await sessionSensitiveAuthApi.getProfile();
            if (response === null || response === undefined) {
                return null;
            }
            return SessionUserProfileDetailedResponseSchema.parse(response);
        },
        enabled: !!user, // Only fetch if user is logged in
    });

    const setEmailsMutation = useMutation({
        mutationFn: authApi.setEmails,
        onSuccess: () => {
            runInBackground(queryClient.invalidateQueries({ queryKey: ['profile'] }), (err) => {
                log.error('Error invalidating queries', err);
            });
            setNewEmail('');
            setShowAddForm(false);
        },
    });
    const requestAddEmailMutation = useMutation({
        mutationFn: authApi.requestAddEmailVerification,
        onSuccess: (data) => {
            setAddEmailMessage(data.message);
            setNewEmail('');
            setShowAddForm(false);
        },
    });

    const normalizedNewEmail: string = normalizeUserEmailInputForSubmission(newEmail);
    const hasValidNewEmail: boolean = UserEmailIdentitySchema.safeParse(normalizedNewEmail).success;
    const isExactDuplicateEmail: boolean =
        profile?.emails.includes(normalizedNewEmail) ?? false;
    let addEmailDisabledReason: string | undefined;
    if (requestAddEmailMutation.isPending) {
        addEmailDisabledReason = 'A verification email request is already in progress.';
    } else if (normalizedNewEmail.length === 0) {
        addEmailDisabledReason = 'Enter an email address.';
    } else if (!hasValidNewEmail) {
        addEmailDisabledReason = 'Enter a valid email address.';
    } else if (isExactDuplicateEmail) {
        addEmailDisabledReason = 'This email address is already on your account.';
    }

    const updateNewEmail = (emailAddress: string): void => {
        setNewEmail(normalizeUserEmailInputForSubmission(emailAddress));
    };

    const handleAddEmail = (e: React.FormEvent) => {
        e.preventDefault();
        if (addEmailDisabledReason === undefined && profile) {
            requestAddEmailMutation.mutate({
                email: normalizedNewEmail,
            });
        }
    };

    if (isLoading) {
        return (
            <div>
                <h1 className="text-3xl font-bold mb-8">Email Addresses</h1>
                <p>Loading...</p>
            </div>
        );
    }

    if (error || !profile) {
        return (
            <div>
                <h1 className="text-3xl font-bold mb-8">Email Addresses</h1>
                <Alert variant="destructive">
                    <AlertDescription>
                        {error instanceof Error ? error.message : 'Failed to load profile'}
                    </AlertDescription>
                </Alert>
            </div>
        );
    }

    return (
        <div>
            <BackButton href="/profile" tooltip="Back to Profile" />
            <div className="flex items-center gap-4 mb-8">
                <h1 className="text-3xl font-bold">Email Addresses</h1>
            </div>

            <div className="max-w-2xl space-y-6">
                {/* Current Emails */}
                <Card>
                    <CardHeader>
                        <CardTitle>Your Email Addresses</CardTitle>
                        <CardDescription>
                            Manage your email addresses. Your primary email is used for
                            notifications and login.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {profile.emails.map((email) => (
                            <div
                                key={email}
                                className="flex items-center justify-between p-4 border rounded-lg"
                            >
                                <div className="flex items-center gap-3">
                                    <span className="font-mono text-sm">{email}</span>
                                    {email === profile.primary_email && (
                                        <Badge variant="default">Primary</Badge>
                                    )}
                                </div>
                                <div className="flex gap-2">
                                    {email !== profile.primary_email && (
                                        <>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() =>
                                                    setEmailsMutation.mutate({
                                                        emails: profile.emails,
                                                        primary_email: email,
                                                    })
                                                }
                                                disabled={setEmailsMutation.isPending}
                                            >
                                                Make Primary
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => {
                                                    setEmailToRemove(email);
                                                    setShowRemoveEmailConfirm(true);
                                                }}
                                                disabled={
                                                    setEmailsMutation.isPending ||
                                                    profile.emails.length === 1
                                                }
                                            >
                                                Remove
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}

                        {setEmailsMutation.error && (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {setEmailsMutation.error instanceof Error
                                        ? setEmailsMutation.error.message
                                        : 'Operation failed'}
                                </AlertDescription>
                            </Alert>
                        )}
                    </CardContent>
                </Card>

                {/* Add New Email */}
                <Card>
                    <CardHeader>
                        <CardTitle>Add Email Address</CardTitle>
                        <CardDescription>
                            Add an additional email address to your account
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {!showAddForm ? (
                            <Button onClick={() => setShowAddForm(true)}>Add Email</Button>
                        ) : (
                            <form onSubmit={handleAddEmail} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="newEmail">New Email Address</Label>
                                    <Input
                                        id="newEmail"
                                        type="email"
                                        value={newEmail}
                                        onChange={(e) => updateNewEmail(e.currentTarget.value)}
                                        onBlur={(e) => updateNewEmail(e.currentTarget.value)}
                                        placeholder="newemail@example.com"
                                        required
                                        disabled={requestAddEmailMutation.isPending}
                                    />
                                    <p className="text-sm text-muted-foreground">
                                        A verification email will be sent to this address
                                    </p>
                                </div>

                                {requestAddEmailMutation.error && (
                                    <Alert variant="destructive">
                                        <AlertDescription>
                                            {requestAddEmailMutation.error instanceof Error
                                                ? requestAddEmailMutation.error.message
                                                : 'Failed to add email'}
                                        </AlertDescription>
                                    </Alert>
                                )}
                                {addEmailMessage !== null && (
                                    <Alert>
                                        <AlertDescription>{addEmailMessage}</AlertDescription>
                                    </Alert>
                                )}

                                <div className="flex gap-2">
                                    <Button
                                        type="submit"
                                        disabled={addEmailDisabledReason !== undefined}
                                        title={addEmailDisabledReason}
                                    >
                                        {requestAddEmailMutation.isPending
                                            ? 'Sending...'
                                            : 'Send Verification Email'}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => {
                                            setShowAddForm(false);
                                            setNewEmail('');
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </form>
                        )}
                    </CardContent>
                </Card>

                {/* Info Card */}
                <Card className="bg-muted">
                    <CardContent className="pt-6">
                        <h3 className="font-semibold mb-2">About Email Verification</h3>
                        <p className="text-sm text-muted-foreground">
                            When you add a new email address, you'll need to verify it before it can
                            be used for login or set as your primary email. Check your inbox for a
                            verification link.
                        </p>
                    </CardContent>
                </Card>
            </div>

            {/* Remove Email Confirmation Modal */}
            {emailToRemove && (
                <ConfirmationModal
                    open={showRemoveEmailConfirm}
                    onOpenChange={(open) => {
                        setShowRemoveEmailConfirm(open);
                        if (!open) {
                            setEmailToRemove(null);
                        }
                    }}
                    message={`Remove ${emailToRemove}?`}
                    onConfirm={() => {
                        if (emailToRemove && profile) {
                            const updatedEmails = profile.emails.filter(
                                (email: string) => email !== emailToRemove
                            );
                            setEmailsMutation.mutate({
                                emails: updatedEmails,
                                primary_email: profile.primary_email,
                            });
                            setEmailToRemove(null);
                        }
                    }}
                    confirmLabel="Remove"
                    isLoading={setEmailsMutation.isPending}
                />
            )}
        </div>
    );
}

export default function EmailsPage() {
    return (
        <ProtectedRoute>
            <MainLayout>
                <EmailsContent />
            </MainLayout>
        </ProtectedRoute>
    );
}
